import { Input } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { DatePicker, Divider, Modal } from "rsuite";
import { useNotification } from "../../../../../utils/notification";
import { selector } from "../../../../../api";
import moment from "moment";
import { ThousandSeparatorInput } from "../../../../../utils/inputNumber";
import { char, code, number } from "../../../../../utils";
import Confirmation from "../../../../../utils/confirmation";
import { gatewayCRUD } from "../../../../../actions/gateway";

const discountSchema = Yup.object().shape({
  percentage: Yup.number()
    .typeError("Percentage must be a number")
    .required("Percentage is required")
    .min(0.01, "Percentage cannot be negative")
    .max(100, "Percentage cannot exceed 100%")
    .test(
      "max-decimals",
      "Maximum 2 decimal places allowed",
      (value) =>
        value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString()),
    ),
});

const initialValues = {
  percentage: "",
  amount: "",
};

export const StudentInvoiceDiscountAdd = (props) => {
  const { open, setOpen, dataId, dataAmount } = props;
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(0);
  const openNotification = useNotification();
  const user = selector.User();

  const formik = useFormik({
    initialValues,
    validationSchema: discountSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { percentage, amount } = values;
      const created_by = user.id;
      const data = {
        percentage: parseFloat(percentage),
        amount: parseFloat(amount),
        logs: dataId,
        created_by,
      };

      setLoading(true);
      setTimeout(() => {
        gatewayCRUD
          .addInvoiceDiscount(data)
          .then(() => {
            openNotification(
              "success",
              "Success",
              "Thank you....! invoice discount has been added",
            );
            setIndex(index + 1);
            resetForm();
            setLoading(false);
            setSubmitting(false);
            setOpen(false);
          })
          .catch((error) => {
            setLoading(false);
            setSubmitting(false);
            const responseData = error?.response?.data;
            let errorMessage = "Something went wrong. Please try again.";

            if (typeof responseData === "string") errorMessage = responseData;
            else if (responseData?.error) errorMessage = responseData.error;
            else if (responseData?.detail) errorMessage = responseData.detail;
            else if (responseData?.message) errorMessage = responseData.message;
            else if (typeof responseData === "object") {
              const firstKey = Object.keys(responseData)[0];
              const firstError = responseData[firstKey];
              if (Array.isArray(firstError)) errorMessage = firstError[0];
            }

            openNotification("error", "Error", errorMessage);
          });
      }, 2000);
    },
  });

  useEffect(() => {
    const percentage = parseFloat(formik.values.percentage);

    if (!percentage || !dataAmount) return;

    const calculatedAmount = (percentage / 100) * dataAmount;

    if (formik.values.amount !== calculatedAmount) {
      formik.setFieldValue("amount", calculatedAmount);
    }
  }, [formik.values.percentage, dataAmount]);

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const handleConfirm = () => {
    Confirmation(
      `Dear ${char.toTitleCase(code.getFirstWord(user?.first_name))},`,
      "Do you want to apply the discount...?",
      handleSubmit,
      null,
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>New Discount</Modal.Title>
        </Modal.Header>
        <Divider />
        <form>
          <Modal.Body>
            <div className="container">
              <div className="row align-items-center mb-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    Discount Percentage <span className="text-danger">*</span>
                  </label>
                  <small
                    className="text-muted d-block fst-italic"
                    style={{ fontSize: "10px" }}
                  >
                    Please enter discount percentage to be applied
                  </small>
                </div>
                <div className="col-md-8 position-relative">
                  <ThousandSeparatorInput
                    className={`form-control-custom pe-5 ${
                      formik.touched.percentage && formik.errors.percentage
                        ? "error"
                        : formik.touched.percentage &&
                            !formik.errors.percentage &&
                            formik.values.percentage
                          ? "success"
                          : ""
                    }`}
                    value={formik.values.percentage}
                    size="large"
                    placeholder="Enter discount percentage"
                    onChange={(e) => {
                      formik.setFieldValue(
                        "percentage",
                        parseFloat(e.target.value),
                      );
                    }}
                    onBlur={() => formik.setFieldTouched("percentage", true)}
                  />
                  {formik.touched.percentage && formik.errors.percentage && (
                    <i className="bi bi-x-circle-fill text-danger form-control-custom-icon-error" />
                  )}
                  {formik.touched.percentage &&
                    !formik.errors.percentage &&
                    formik.values.percentage && (
                      <i className="bi bi-check-circle-fill text-success form-control-custom-icon-success" />
                    )}
                  {formik.touched.percentage && formik.errors.percentage && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {formik.errors.percentage}
                    </div>
                  )}
                </div>
              </div>
              <div className="row align-items-center mb-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Invoice Amount</label>
                  <small
                    className="text-muted d-block fst-italic"
                    style={{ fontSize: "10px" }}
                  >
                    Invoice amount before discount.
                  </small>
                </div>
                <div className="col-md-8 position-relative">
                  <span className="text- fw-bold h4">
                    {number.fNumberDoublePoint(dataAmount)}
                  </span>
                </div>
              </div>
              <div className="row align-items-center mb-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Discount Amount</label>
                  <small
                    className="text-muted d-block fst-italic"
                    style={{ fontSize: "10px" }}
                  >
                    Calculated discount amount based on the percentage and
                    invoice amount.
                  </small>
                </div>
                <div className="col-md-8 position-relative">
                  <span className="text-success fw-bold h4">
                    {number.fNumberDoublePoint(
                      (parseFloat(formik.values.percentage) / 100) * dataAmount,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Divider style={{ marginTop: -10 }} />
          <Modal.Footer>
            <div className="container">
              <div className="row">
                <div className="col-md-6 col-sm-12 d-flex justify-content-start">
                  <span className="fs-18 text-danger"> * &nbsp;</span> Reuired
                  field
                </div>
                <div className="col-md-6 col-sm-12 d-flex justify-content-end">
                  <button
                    onClick={() => setOpen(false)}
                    type="button"
                    disabled={loading}
                    className="btn btn-soft-danger btn-label waves-effect waves-light rounded-pill me-3"
                  >
                    <i class="ri-forbid-line label-icon align-middle rounded-pill fs-16 me-2"></i>{" "}
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-soft-success btn-label waves-effect waves-light rounded-pill"
                    disabled={formik.isSubmitting || !formik.isValid}
                    onClick={() => {
                      handleConfirm();
                    }}
                  >
                    {!loading && (
                      <span className="indicator-label">
                        <i class="ri-check-double-line label-icon align-middle rounded-pill fs-16 me-2"></i>{" "}
                        Save
                      </span>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: "block" }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      {loading && (
        <div className="processing-overlay">
          <div className="processing-card">
            <div className="processing-spinner"></div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Saving Data...</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Please do not close this window
            </div>
          </div>
        </div>
      )}
    </>
  );
};
